// Growth › Analytics: /growth/analytics?days=7|30|90
//
// Two reads kept apart, so the page works with Google Analytics unset or down:
// scorelm.com's numbers (cached by the server, at most an hour old) and
// ScoreLM's funnel, always from our own tables. One series per chart, in the
// accent, through the kit's charts: each has a screen-reader table, a keyboard
// crosshair, and fits a phone. Two measures are two charts, never two axes.

import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { GA_RANGES, type GaRange, type GaReport, type SiteAnalytics } from '@/api/growthInsights'
import { PageHeader } from '@/components/shell/PageHeader'
import { BarList, Card, CardHeader, CopyButton, ErrorState, FilterChips, InlineAlert, LineChart, LoadingState, RelativeTime, StatTile, type BarListItem } from '@/components/ui'
import { formatCount } from '@/lib/format'
import { INQUIRY_STATUS_LABELS, LEAD_STATUS_LABELS } from '@/lib/growth'
import { useDocumentTitle } from '@/lib/hooks'
import { useQuery } from '@/lib/query'

const TILES = 'grid grid-cols-2 gap-3 lg:grid-cols-4'

function readDays(p: URLSearchParams): GaRange {
  const days = Number(p.get('days'))
  return (GA_RANGES as readonly number[]).includes(days) ? (days as GaRange) : 30
}

function ChartCard({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} description={description} />
      {children}
    </Card>
  )
}

const TOTALS_NOTE = 'Google Analytics gives totals, not who. It can’t tell us which person or league visited, and this page doesn’t try to find out.'

function Setup({ site }: { site: Extract<SiteAnalytics, { configured: false }> }) {
  return (
    <Card>
      <CardHeader title="Connect Google Analytics" description="scorelm.com sends its traffic to Google Analytics. This page reads it once these are set on the backend." />
      <div className="grid gap-3 text-[13.5px] text-ink">
        {site.problem ? <InlineAlert tone="warn">{site.problem}</InlineAlert> : null}
        <div>
          <p className="text-xs font-semibold text-muted">Set on the backend</p>
          <ul className="mt-1.5 grid gap-1.5">
            {site.missing.map((name) => (
              <li key={name} className="flex flex-wrap items-center gap-2">
                <code className="break-all rounded bg-hover px-1.5 py-0.5 font-mono text-xs">{name}</code>
                <CopyButton value={name} ariaLabel={`Copy ${name}`} />
              </li>
            ))}
          </ul>
        </div>
        <ol className="grid list-decimal gap-1.5 pl-5 text-[13px] text-muted">
          {site.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <p className="text-xs text-muted">Read only. Everything else on this page works without it.</p>
      </div>
    </Card>
  )
}

function SiteReport({ report }: { report: GaReport }) {
  const { totals } = report
  return (
    <>
      <div className={TILES}>
        <StatTile label="Visitors" value={formatCount(totals.visitors)} />
        <StatTile label="Sessions" value={formatCount(totals.sessions)} />
        <StatTile label="Page views" value={formatCount(totals.page_views)} />
        <StatTile label="Conversions" value={formatCount(totals.conversions)} hint="Key events in Google Analytics" />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Visitors per day">
          <LineChart data={report.trend.map((d) => ({ date: d.date, views: d.visitors }))} label="Visitors per day" unit="visitors" />
        </ChartCard>
        <ChartCard title="Sessions per day">
          <LineChart data={report.trend.map((d) => ({ date: d.date, views: d.sessions }))} label="Sessions per day" unit="sessions" />
        </ChartCard>
        <ChartCard title="Top pages" description="By page views">
          <BarList label="Top pages by page views" valueLabel="Page views" items={report.pages.map((p) => ({ label: p.path, value: p.views }))} emptyText="No page views in this period." />
        </ChartCard>
        <ChartCard title="Sources" description="By sessions">
          <BarList label="Sources by sessions" valueLabel="Sessions" items={report.sources.map((s) => ({ label: s.source, value: s.sessions }))} emptyText="No sessions in this period." />
        </ChartCard>
        <ChartCard title="Countries" description="By visitors">
          <BarList label="Countries by visitors" valueLabel="Visitors" items={report.countries.map((c) => ({ label: c.country, value: c.visitors }))} emptyText="No visitors in this period." />
        </ChartCard>
        <ChartCard title="Devices" description="By visitors">
          <BarList label="Devices by visitors" valueLabel="Visitors" items={report.devices.map((d) => ({ label: d.device, value: d.visitors }))} emptyText="No visitors in this period." />
        </ChartCard>
        <ChartCard title="Conversions" description="By key event">
          <BarList
            label="Conversions by key event"
            valueLabel="Count"
            items={report.conversions.map((c) => ({ label: c.event, value: c.count }))}
            emptyText="None yet. Mark an event as a key event in Google Analytics and it shows here."
          />
        </ChartCard>
      </div>
    </>
  )
}

function WebsiteSection({ days }: { days: GaRange }) {
  const { data: site, error, status, refetch } = useQuery(['growth', 'analytics', 'site', days], (signal) => api.growth.analytics.site(days, signal))

  let body: ReactNode
  if (status === 'loading' && !site) body = <LoadingState label="Loading scorelm.com…" />
  else if (!site) body = <Card><ErrorState error={error} title="Couldn't load scorelm.com's numbers" onRetry={refetch} /></Card>
  else if (!site.configured) body = <Setup site={site} />
  else {
    body = (
      <>
        {site.error ? <InlineAlert tone={site.report ? 'warn' : 'bad'} title={site.report ? 'Showing the last good numbers' : "Couldn't read Google Analytics"}>{site.error}</InlineAlert> : null}
        {site.report ? <SiteReport report={site.report} /> : null}
        {site.fetched_at ? (
          <p className="text-xs text-muted">
            From Google Analytics <RelativeTime value={site.fetched_at} />. Refreshed at most once an hour.
          </p>
        ) : null}
      </>
    )
  }

  return (
    <section aria-labelledby="growth-site" className="grid grid-cols-1 gap-4">
      <div className="grid gap-1">
        <h2 id="growth-site" className="font-display text-lg font-extrabold uppercase tracking-[0.02em] text-ink">scorelm.com</h2>
        <p className="text-[13px] text-muted">{TOTALS_NOTE}</p>
      </div>
      {body}
    </section>
  )
}

function Funnel({ days }: { days: GaRange }) {
  const { data, error, status, refetch } = useQuery(['growth', 'analytics', 'funnel', days], (signal) => api.growth.analytics.funnel(days, signal))

  let body: ReactNode
  if (status === 'loading' && !data) body = <LoadingState label="Loading the funnel…" />
  else if (!data) body = <Card><ErrorState error={error} title="Couldn't load the funnel" onRetry={refetch} /></Card>
  else {
    const { funnel, campaigns, league_traffic } = data
    const statuses = (labels: Record<string, string>, counts: Record<string, number>): BarListItem[] => Object.keys(labels).map((k) => ({ label: labels[k], value: counts[k] ?? 0 }))
    body = (
      <>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <StatTile label="Self-serve signups" value={formatCount(funnel.signups)} to="/tenants?source=self_serve" />
          <StatTile label="Trials" value={formatCount(funnel.trials_started)} hint={`${formatCount(funnel.trials_now)} on trial now`} />
          <StatTile label="Went live" value={formatCount(funnel.went_live)} hint={`${formatCount(funnel.live_now)} live now`} />
          <StatTile label="Inquiries" value={formatCount(funnel.inquiries)} to="/growth/inquiries" />
          <StatTile label="Leads added" value={formatCount(funnel.leads_added)} to="/growth/leads" />
        </div>
        <p className="text-xs text-muted">Trials and Went live count leagues created in this period. A league is live on a paid or manual plan.</p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartCard title="Leads by status" description="All leads, today">
            <BarList label="Leads by status" valueLabel="Leads" items={statuses(LEAD_STATUS_LABELS, data.leads_by_status)} />
          </ChartCard>
          <ChartCard title="Inquiries by status" description="All inquiries, today">
            <BarList label="Inquiries by status" valueLabel="Inquiries" items={statuses(INQUIRY_STATUS_LABELS, data.inquiries_by_status)} />
          </ChartCard>
          <ChartCard title="Campaigns" description="Email campaigns to leads">
            {campaigns.available ? (
              <div className="grid grid-cols-2 gap-3">
                <StatTile label="Sent" value={formatCount(campaigns.sent)} hint={`${formatCount(campaigns.campaigns)} campaigns`} />
                <StatTile label="Clicked" value={formatCount(campaigns.clicked)} />
                <StatTile label="Replied" value={formatCount(campaigns.replied)} />
                <StatTile label="Bounced" value={formatCount(campaigns.bounced)} hint={`${formatCount(campaigns.unsubscribed)} unsubscribed`} />
              </div>
            ) : (
              <p className="text-xs text-muted">No campaign stats yet. They show here once campaigns are sending.</p>
            )}
          </ChartCard>
          <ChartCard title="League sites" description={`${formatCount(league_traffic.total_views)} page views across all league sites`}>
            <BarList
              label="League sites by page views"
              valueLabel="Page views"
              items={league_traffic.leagues.map((l) => ({ label: l.name, value: l.views }))}
              emptyText="No page views on league sites in this period."
            />
          </ChartCard>
        </div>
      </>
    )
  }

  return (
    <section aria-labelledby="growth-funnel" className="grid grid-cols-1 gap-4">
      <div className="grid gap-1">
        <h2 id="growth-funnel" className="font-display text-lg font-extrabold uppercase tracking-[0.02em] text-ink">ScoreLM funnel</h2>
        <p className="text-[13px] text-muted">From our own data.</p>
      </div>
      {body}
    </section>
  )
}

export default function AnalyticsPage() {
  useDocumentTitle('Analytics')
  const [params, setParams] = useSearchParams()
  const days = readDays(params)
  return (
    <div className="grid grid-cols-1 gap-6">
      <PageHeader title="Analytics" description="How people find ScoreLM, and what they do next." />
      <FilterChips
        label="Period"
        options={GA_RANGES.map((d) => ({ value: String(d), label: `${d} days` }))}
        value={String(days)}
        onChange={(v) => setParams(v === '30' ? {} : { days: v }, { replace: true })}
      />
      <WebsiteSection days={days} />
      <Funnel days={days} />
    </div>
  )
}
